/**
 * POS API Service
 * Direct REST API calls to backend (no IPC, no local database)
 */
import { getTimePartsInTz, toDateString, VENUE_TIMEZONE } from '../lib/timezone';
const API_BASE = process.env.REACT_APP_API_BASE !== undefined ? process.env.REACT_APP_API_BASE : 'http://localhost:8080';
// Logged once on module load
if (typeof window !== 'undefined') {
    console.log('[POS API] API_BASE:', API_BASE);
}
export async function listBookings(filters) {
    const params = new URLSearchParams();
    if (filters?.startDate)
        params.append('startDate', filters.startDate);
    if (filters?.endDate)
        params.append('endDate', filters.endDate);
    if (filters?.roomId)
        params.append('roomId', filters.roomId);
    if (filters?.status)
        params.append('status', filters.status);
    if (filters?.page)
        params.append('page', filters.page.toString());
    if (filters?.limit)
        params.append('limit', filters.limit.toString());
    const url = `${API_BASE}/api/bookings?${params.toString()}`;
    const res = await fetch(url, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok) {
        const errorText = await res.text();
        console.error('[POS API] Bookings error:', errorText);
        throw new Error(`Failed to fetch bookings: ${res.status} ${errorText}`);
    }
    const json = await res.json();
    return json.bookings || [];
}
export async function getBooking(id) {
    const res = await fetch(`${API_BASE}/api/bookings/${id}`, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok)
        throw new Error('Failed to fetch booking');
    const json = await res.json();
    const rawBooking = json.booking;
    // Transform backend data to frontend format.
    // CRITICAL: derive date and time in the venue timezone (Halifax),
    // never the browser's local timezone. Admins/staff may view this
    // page from any timezone (e.g. dev sensors set to Pacific) and
    // the wall-clock booking time must always be Halifax.
    const startTime = new Date(rawBooking.startTime);
    const endTime = new Date(rawBooking.endTime);
    const duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60));
    const date = toDateString(startTime); // YYYY-MM-DD in Halifax
    const { hours, minutes } = getTimePartsInTz(startTime, VENUE_TIMEZONE);
    const time = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`; // HH:MM Halifax
    return {
        ...rawBooking,
        date,
        time,
        duration: duration,
        roomName: rawBooking.roomName || `Room ${rawBooking.roomId}`, // Fallback if not provided
    };
}
export async function createBooking(data) {
    const res = await fetch(`${API_BASE}/api/bookings/simple/create`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify(data)
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to create booking' }));
        const bookingError = new Error(error.error || 'Failed to create booking');
        bookingError.details = error.details;
        bookingError.status = res.status;
        throw bookingError;
    }
    const json = await res.json();
    return json.booking;
}
export async function createQuickSale() {
    const res = await fetch(`${API_BASE}/api/bookings/simple/quick-sale`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to create quick sale' }));
        throw new Error(error.error || 'Failed to create quick sale');
    }
    const json = await res.json();
    return json.booking;
}
export async function updateBookingStatus(id, status) {
    const res = await fetch(`${API_BASE}/api/bookings/${id}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({ status })
    });
    if (!res.ok)
        throw new Error('Failed to update booking status');
}
export async function updateBookingPlayers(id, players) {
    const res = await fetch(`${API_BASE}/api/bookings/${id}/players`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({ players })
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to update players' }));
        throw new Error(error.error || 'Failed to update players');
    }
    const json = await res.json();
    return json.booking;
}
export async function cancelBooking(id) {
    const res = await fetch(`${API_BASE}/api/bookings/${id}/cancel`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok)
        throw new Error('Failed to cancel booking');
}
export async function extendBooking(id) {
    const res = await fetch(`${API_BASE}/api/bookings/${id}/extend`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production',
        },
    });
    if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to extend booking');
    }
    const json = await res.json();
    return json.booking;
}
export async function listRooms() {
    const url = `${API_BASE}/api/bookings/rooms`;
    const res = await fetch(url, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok) {
        const errorText = await res.text();
        console.error('[POS API] Rooms error:', errorText);
        throw new Error(`Failed to fetch rooms: ${res.status} ${errorText}`);
    }
    const json = await res.json();
    if (json.rooms && json.rooms.length > 0) {
        console.log('[POS API] Loaded', json.rooms.length, 'rooms');
    }
    return json.rooms || [];
}
export async function updateRoomStatus(id, status) {
    const res = await fetch(`${API_BASE}/api/bookings/rooms/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({ status })
    });
    if (!res.ok)
        throw new Error('Failed to update room status');
}
export async function listMenuItems() {
    const res = await fetch(`${API_BASE}/api/menu/items`, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok)
        throw new Error('Failed to fetch menu items');
    const json = await res.json();
    return json.items || [];
}
export async function createMenuItem(data) {
    const res = await fetch(`${API_BASE}/api/menu/items`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify(data)
    });
    if (!res.ok)
        throw new Error('Failed to create menu item');
    const json = await res.json();
    return json.item;
}
export async function updateMenuItem(id, data) {
    const res = await fetch(`${API_BASE}/api/menu/items/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify(data)
    });
    if (!res.ok)
        throw new Error('Failed to update menu item');
    const json = await res.json();
    return json.item;
}
export async function deleteMenuItem(id) {
    const res = await fetch(`${API_BASE}/api/menu/items/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok)
        throw new Error('Failed to delete menu item');
}
// ============= Settings API =============
export async function getGlobalTaxRate() {
    const res = await fetch(`${API_BASE}/api/settings/global_tax_rate`, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok)
        return 8; // default fallback
    const json = await res.json();
    // Backend returns { taxRate: number, key: string, updatedAt?: string }
    return json.taxRate || 8;
}
export async function updateGlobalTaxRate(rate) {
    const res = await fetch(`${API_BASE}/api/settings/global_tax_rate`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        // Backend expects { taxRate: number }
        body: JSON.stringify({ taxRate: rate })
    });
    if (!res.ok)
        throw new Error('Failed to update tax rate');
}
export async function getClosedDates() {
    const res = await fetch(`${API_BASE}/api/settings/closed_dates`, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok)
        return [];
    const json = await res.json();
    return Array.isArray(json.closedDates) ? json.closedDates : [];
}
export async function updateClosedDates(dates) {
    const res = await fetch(`${API_BASE}/api/settings/closed_dates`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({ closedDates: dates })
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to update closed dates');
    }
}
// Preview which active bookings exist on a date (before closing it).
export async function getClosureImpact(date) {
    const res = await fetch(`${API_BASE}/api/bookings/closure-impact?date=${encodeURIComponent(date)}`, {
        credentials: 'include',
    });
    if (!res.ok)
        return [];
    const json = await res.json();
    return Array.isArray(json.bookings) ? json.bookings : [];
}
// Cancel every active booking on a date and email affected customers. Admin only.
export async function cancelBookingsForClosure(date, reason) {
    const res = await fetch(`${API_BASE}/api/bookings/cancel-for-closure`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, reason }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to cancel bookings for closure');
    }
    const json = await res.json();
    return { cancelled: json.cancelled ?? 0, emailed: json.emailed ?? 0 };
}
/**
 * Get all invoices for a booking (includes orders for each seat)
 */
export async function getInvoices(bookingId) {
    const res = await fetch(`${API_BASE}/api/bookings/${bookingId}/invoices`, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok) {
        const errorText = await res.text();
        console.error('[POS API] Get invoices error:', errorText);
        throw new Error(`Failed to fetch invoices: ${res.status} ${errorText}`);
    }
    const json = await res.json();
    return json.invoices || [];
}
/**
 * Create a new order for a booking
 */
export async function createOrder(data) {
    const body = {
        seatIndex: data.seatIndex,
        quantity: data.quantity,
    };
    // Add either menuItemId or custom item fields
    if (data.menuItemId) {
        body.menuItemId = data.menuItemId;
    }
    else {
        body.customItemName = data.customItemName;
        body.customItemPrice = data.customItemPrice;
    }
    // Add discount type if present
    if (data.discountType) {
        body.discountType = data.discountType;
    }
    // Add tax exempt flag if present
    if (data.taxExempt) {
        body.taxExempt = data.taxExempt;
    }
    // Gift card sale (pending — issued on payment settle)
    if (data.giftCardSale) {
        body.giftCardSale = true;
        if (data.giftCardRecipientEmail)
            body.giftCardRecipientEmail = data.giftCardRecipientEmail;
    }
    const res = await fetch(`${API_BASE}/api/bookings/${data.bookingId}/orders`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify(body)
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to create order' }));
        throw new Error(error.error || 'Failed to create order');
    }
    const json = await res.json();
    return json;
}
/**
 * Update order quantity
 */
export async function updateOrder(orderId, quantity) {
    const res = await fetch(`${API_BASE}/api/bookings/orders/${orderId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({ quantity })
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to update order' }));
        throw new Error(error.error || 'Failed to update order');
    }
    const json = await res.json();
    return json;
}
/**
 * Delete an order
 */
export async function deleteOrder(orderId) {
    const res = await fetch(`${API_BASE}/api/bookings/orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to delete order' }));
        throw new Error(error.error || 'Failed to delete order');
    }
    const json = await res.json();
    return json;
}
/**
 * Mark an invoice as paid
 */
export async function payInvoice(data) {
    const res = await fetch(`${API_BASE}/api/bookings/invoices/${data.invoiceId}/pay`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({
            bookingId: data.bookingId,
            seatIndex: data.seatIndex,
            paymentMethod: data.paymentMethod,
            tip: data.tip,
            payments: data.payments,
        })
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to process payment' }));
        throw new Error(error.error || 'Failed to process payment');
    }
    const json = await res.json();
    return json;
}
/**
 * Add a single partial payment to an invoice (incremental collect)
 */
export async function addPayment(data) {
    const res = await fetch(`${API_BASE}/api/bookings/invoices/${data.invoiceId}/add-payment`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({
            bookingId: data.bookingId,
            seatIndex: data.seatIndex,
            method: data.method,
            amount: data.amount,
            tip: data.tip,
            tipMethod: data.tipMethod,
        })
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to add payment' }));
        throw new Error(error.error || 'Failed to add payment');
    }
    const json = await res.json();
    return json;
}
/**
 * Cancel payment/refund an invoice
 */
export async function unpayInvoice(data) {
    const res = await fetch(`${API_BASE}/api/bookings/invoices/${data.invoiceId}/unpay`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({
            bookingId: data.bookingId,
        })
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to cancel payment' }));
        throw new Error(error.error || 'Failed to cancel payment');
    }
    const json = await res.json();
    return json;
}
/**
 * Set (or clear) a per-seat tax rate override on an invoice.
 * @param taxRate percent (0-100), or null to reset the seat back to the global rate.
 * Returns the recalculated invoices for the booking.
 */
export async function setSeatTaxRate(data) {
    const res = await fetch(`${API_BASE}/api/bookings/${data.bookingId}/seats/${data.seatIndex}/tax`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({ taxRate: data.taxRate })
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to update tax rate' }));
        throw new Error(error.error || 'Failed to update tax rate');
    }
    const json = await res.json();
    return json.invoices || [];
}
/**
 * Get payment status for a booking
 */
export async function getPaymentStatus(bookingId) {
    const res = await fetch(`${API_BASE}/api/bookings/${bookingId}/payment-status`, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok) {
        const errorText = await res.text();
        console.error('[POS API] Get payment status error:', errorText);
        throw new Error(`Failed to fetch payment status: ${res.status} ${errorText}`);
    }
    const json = await res.json();
    return json;
}
/**
 * Get full receipt for a booking
 */
export async function getReceipt(bookingId) {
    const res = await fetch(`${API_BASE}/api/receipts/${bookingId}`, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok) {
        const errorText = await res.text();
        console.error('[POS API] Get receipt error:', errorText);
        throw new Error(`Failed to fetch receipt: ${res.status} ${errorText}`);
    }
    const json = await res.json();
    return json.receipt;
}
/**
 * Get receipt for a specific seat
 */
export async function getSeatReceipt(bookingId, seatIndex) {
    const res = await fetch(`${API_BASE}/api/receipts/${bookingId}/seat/${seatIndex}`, {
        credentials: 'include',
        headers: { 'x-pos-admin-key': 'pos-dev-key-change-in-production' }
    });
    if (!res.ok) {
        const errorText = await res.text();
        console.error('[POS API] Get seat receipt error:', errorText);
        throw new Error(`Failed to fetch seat receipt: ${res.status} ${errorText}`);
    }
    const json = await res.json();
    return json.receipt;
}
/**
 * Send receipt via email
 */
export async function sendReceiptEmail(bookingId, email, seatIndex) {
    const res = await fetch(`${API_BASE}/api/receipts/${bookingId}/email`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'x-pos-admin-key': 'pos-dev-key-change-in-production'
        },
        body: JSON.stringify({ email, seatIndex })
    });
    if (!res.ok) {
        const errorText = await res.text();
        console.error('[POS API] Send receipt email error:', errorText);
        throw new Error(`Failed to send receipt email: ${res.status} ${errorText}`);
    }
    const json = await res.json();
    return json;
}
// ── Kiosk (no auth needed) ──
export async function clockIn(pin) {
    const res = await fetch(`${API_BASE}/api/time-entries/clock-in`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Clock-in failed');
    return json;
}
export async function clockOut(pin) {
    const res = await fetch(`${API_BASE}/api/time-entries/clock-out`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Clock-out failed');
    return json;
}
export async function checkClockStatus(pin) {
    const res = await fetch(`${API_BASE}/api/time-entries/status`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Status check failed');
    return json;
}
export async function getActiveTimeEntries() {
    const res = await fetch(`${API_BASE}/api/time-entries/active`, {
        credentials: 'include',
    });
    const json = await res.json().catch(() => ({ error: 'Failed to load active time entries' }));
    if (!res.ok)
        throw new Error(json.error || 'Failed to load active time entries');
    return json.entries || [];
}
export async function verifyManager(pin) {
    const res = await fetch(`${API_BASE}/api/employees/verify-manager`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.reason || 'Verification failed');
    return json;
}
// ── Admin endpoints ──
export async function listEmployees(activeOnly = false) {
    const params = activeOnly ? '?active=true' : '';
    const res = await fetch(`${API_BASE}/api/employees${params}`, {
        credentials: 'include',
    });
    if (!res.ok)
        throw new Error('Failed to fetch employees');
    const json = await res.json();
    return json.employees;
}
export async function createEmployee(name, pin, role = 'STAFF') {
    const res = await fetch(`${API_BASE}/api/employees`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, pin, role }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to create employee');
    return json.employee;
}
export async function updateEmployee(id, data) {
    const res = await fetch(`${API_BASE}/api/employees/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to update employee');
    return json.employee;
}
export async function deleteEmployee(id) {
    const res = await fetch(`${API_BASE}/api/employees/${id}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to deactivate employee');
    }
}
export async function listTimeEntries(filters) {
    const params = new URLSearchParams();
    if (filters?.startDate)
        params.append('startDate', filters.startDate);
    if (filters?.endDate)
        params.append('endDate', filters.endDate);
    if (filters?.employeeId)
        params.append('employeeId', filters.employeeId);
    const res = await fetch(`${API_BASE}/api/time-entries?${params.toString()}`, {
        credentials: 'include',
    });
    if (!res.ok)
        throw new Error('Failed to fetch time entries');
    const json = await res.json();
    return json.entries;
}
export async function listActiveTimeEntries() {
    const res = await fetch(`${API_BASE}/api/time-entries/active`, {
        credentials: 'include',
    });
    if (!res.ok)
        throw new Error('Failed to fetch active entries');
    const json = await res.json();
    return json.entries;
}
export async function updateTimeEntry(id, data) {
    const res = await fetch(`${API_BASE}/api/time-entries/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to update time entry');
    return json.entry;
}
export async function getEmployeeHours(params) {
    const query = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
    if (params.employeeId)
        query.set('employeeId', params.employeeId);
    const res = await fetch(`${API_BASE}/api/reports/employee-hours?${query.toString()}`, {
        credentials: 'include',
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to load employee hours');
    return json;
}
export async function uploadReceipt(paymentId, imageBlob) {
    const formData = new FormData();
    formData.append('image', imageBlob, 'receipt.jpg');
    const res = await fetch(`${API_BASE}/api/payments/${paymentId}/receipt`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to upload receipt');
    return json;
}
export async function getReceiptUrl(paymentId) {
    const res = await fetch(`${API_BASE}/api/payments/${paymentId}/receipt`, {
        credentials: 'include',
    });
    if (!res.ok) {
        if (res.status === 404)
            return '';
        throw new Error('Failed to get receipt');
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
}
export async function deleteReceipt(paymentId) {
    const res = await fetch(`${API_BASE}/api/payments/${paymentId}/receipt`, {
        method: 'DELETE',
        credentials: 'include',
    });
    if (!res.ok) {
        const json = await res.json().catch(() => ({ error: 'Failed to delete receipt' }));
        throw new Error(json.error);
    }
}
export async function getPendingReceipts(date) {
    const params = date ? `?date=${date}` : '';
    const res = await fetch(`${API_BASE}/api/payments/pending-receipts${params}`, {
        credentials: 'include',
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to load pending receipts');
    return json;
}
export async function getPaidNotCompletedBookings() {
    const res = await fetch(`${API_BASE}/api/bookings/attention/paid-not-completed`, {
        credentials: 'include',
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to load paid bookings awaiting completion');
    return json.bookings || [];
}
export async function getReceiptAnalysisItems(params = {}) {
    const query = new URLSearchParams();
    if (params.date)
        query.set('date', params.date);
    if (params.needsReview)
        query.set('needsReview', 'true');
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const res = await fetch(`${API_BASE}/api/receipt-analysis${suffix}`, {
        credentials: 'include',
    });
    const json = await res.json().catch(() => ({ error: 'Failed to load receipt analyses' }));
    if (!res.ok)
        throw new Error(json.error || 'Failed to load receipt analyses');
    return json;
}
export async function getReceiptReviewAttentionItems(date, options = {}) {
    const query = new URLSearchParams();
    if (date)
        query.set('date', date);
    if (options.backlog)
        query.set('backlog', 'true');
    const params = query.toString() ? `?${query.toString()}` : '';
    const res = await fetch(`${API_BASE}/api/receipt-analysis/dashboard/needs-review${params}`, {
        credentials: 'include',
    });
    const json = await res.json().catch(() => ({ error: 'Failed to load receipt reviews' }));
    if (!res.ok)
        throw new Error(json.error || 'Failed to load receipt reviews');
    return json;
}
export async function getBookingReceiptOcrStatuses(bookingId) {
    const res = await fetch(`${API_BASE}/api/receipt-analysis/bookings/${encodeURIComponent(bookingId)}/status`, {
        credentials: 'include',
    });
    const json = await res.json().catch(() => ({ error: 'Failed to load booking receipt OCR status' }));
    if (!res.ok)
        throw new Error(json.error || 'Failed to load booking receipt OCR status');
    return json;
}
export async function getReceiptAnalysisSummary(startDate, endDate) {
    const query = new URLSearchParams({ startDate, endDate });
    const res = await fetch(`${API_BASE}/api/receipt-analysis/summary?${query.toString()}`, {
        credentials: 'include',
    });
    const json = await res.json().catch(() => ({ error: 'Failed to load receipt analysis summary' }));
    if (!res.ok)
        throw new Error(json.error || 'Failed to load receipt analysis summary');
    return json;
}
export async function getReceiptReconciliation(startDate, endDate) {
    const res = await fetch(`${API_BASE}/api/reports/receipt-reconciliation?startDate=${startDate}&endDate=${endDate}`, {
        credentials: 'include',
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to load reconciliation data');
    return json;
}
export async function getOcrHealth() {
    const res = await fetch(`${API_BASE}/api/receipt-analysis/health`, {
        credentials: 'include',
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to load OCR health');
    return json;
}
/** Issue (sell) a new gift card. */
export async function issueGiftCard(params) {
    const res = await fetch(`${API_BASE}/api/gift-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error?.formErrors?.join(', ') || json.error || 'Failed to issue gift card');
    return json;
}
/** Validate a gift card by code (balance/status). */
export async function validateGiftCard(code) {
    const res = await fetch(`${API_BASE}/api/gift-cards/validate/${encodeURIComponent(code)}`, {
        credentials: 'include',
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to validate gift card');
    return json;
}
/** Redeem a gift card as tender against a seat invoice. */
export async function redeemGiftCard(params) {
    const res = await fetch(`${API_BASE}/api/gift-cards/${encodeURIComponent(params.code)}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ bookingId: params.bookingId, seatIndex: params.seatIndex, amount: params.amount }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to redeem gift card');
    return json;
}
/** Admin: list gift cards with optional filters. */
export async function listGiftCards(params) {
    const q = new URLSearchParams();
    if (params?.status)
        q.set('status', params.status);
    if (params?.search)
        q.set('search', params.search);
    if (params?.page)
        q.set('page', String(params.page));
    if (params?.limit)
        q.set('limit', String(params.limit));
    const res = await fetch(`${API_BASE}/api/gift-cards?${q.toString()}`, { credentials: 'include' });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to list gift cards');
    return json;
}
/** Admin: gift card detail incl. transaction ledger. */
export async function getGiftCard(id) {
    const res = await fetch(`${API_BASE}/api/gift-cards/${id}`, { credentials: 'include' });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to load gift card');
    return json;
}
/** Admin: void a gift card. */
export async function voidGiftCard(id, note) {
    const res = await fetch(`${API_BASE}/api/gift-cards/${id}/void`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ note }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json.error || 'Failed to void gift card');
    return json;
}
/** Error carrying the API's machine-readable `code` (e.g. MANAGER_PIN_REQUIRED). */
export class ApiError extends Error {
    constructor(message, status, code) {
        super(message);
        this.status = status;
        this.code = code;
    }
}
async function checklistFetch(path, init) {
    const res = await fetch(`${API_BASE}/api/checklists${path}`, {
        credentials: 'include',
        ...init,
        headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        const fieldErrors = json?.details?.fieldErrors ? Object.values(json.details.fieldErrors).flat().join(' · ') : '';
        throw new ApiError(fieldErrors || json.error || 'Checklist request failed', res.status, json.code);
    }
    return json;
}
export const getChecklistToday = (date) => checklistFetch(`/today${date ? `?date=${date}` : ''}`);
export const completeChecklistItem = (taskId, periodKey, pin, note) => checklistFetch(`/${taskId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ pin, periodKey, note: note || undefined }),
});
export const undoChecklistItem = (taskId, periodKey, pin) => checklistFetch(`/${taskId}/undo`, { method: 'POST', body: JSON.stringify({ pin, periodKey }) });
export const listChecklistTasks = () => checklistFetch('/tasks');
export const createChecklistTask = (input) => checklistFetch('/tasks', { method: 'POST', body: JSON.stringify(input) });
export const updateChecklistTask = (id, input) => checklistFetch(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(input) });
export const archiveChecklistTask = (id) => checklistFetch(`/tasks/${id}`, { method: 'DELETE' });
export const loadStarterChecklist = () => checklistFetch('/tasks/starter', { method: 'POST' });
export const listChecklistEmployees = () => checklistFetch('/employees');
/** Admin: the Mon–Sun checklist week containing `date` (default: this week). */
export const getChecklistHistory = (date) => checklistFetch(`/history${date ? `?date=${date}` : ''}`);
